<template>
	<div
		class="game-control"
		:class="wraperClass"
	>
		<!-- controls container -->
		<div
			:class="{'is-active': isActive}"
			class="controls"
		>
			<!-- main area -->
			<div class="main">
				<div class="left">
					<label>{{ homeTeamLabel }}</label>
					<TagButton
						class="tag-button"
						:label="$I18n.trans('tag.names.generic.goal')"
						hide-icon
						:label-shadow="false"
						:enabled="!isTimeEditable"
						:clickable="true"
						@click="handleScoreIncrease(homeTeam)"
					/>
					<TagButton
						class="tag-button"
						:label="$I18n.trans('tag.names.generic.2_min')"
						hide-icon
						:label-shadow="false"
						:enabled="!isTimeEditable"
						:clickable="true"
						@click="handleTwoMinPenalty(homeTeam)"
					/>
					<TagButton
						class="tag-button"
						:label="$I18n.trans('tag.names.generic.5_min')"
						hide-icon
						:label-shadow="false"
						:enabled="!isTimeEditable"
						:clickable="true"
						@click="handleFiveMinPenalty(homeTeam)"
					/>
				</div>
				<div class="center">
					<div class="score">
						<!-- home team score -->
						<span class="score-value">{{ homeTeamScore }}</span>
						<!-- guest team score -->
						<span class="score-value">{{ guestTeamScore }}</span>
						<!-- time input -->
					</div>
					<div
						v-tooltip="timeTooltip"
						class="time-background"
						:class="{
							disabled: isDisabled || !isTimeEditable,
							edited: currentTime.formattedTime !== timeInputValue,
							focused: inputFocused
						}"
					>
						<div
							class="timer-arrow minute-up-arrow"
							:class="{disabled: isDisabled || !isTimeEditable}"
							@click="incrementMinutes()"
						>
							<Icon
								v-if="!isMobileWebApp"
								icon-name="up-arrow"
								size="sm"
							/>
						</div>
						<div
							class="timer-arrow second-up-arrow"
							:class="{disabled: isDisabled || !isTimeEditable}"
							@click="incrementSeconds()"
						>
							<Icon
								v-if="!isMobileWebApp"
								icon-name="up-arrow"
								size="sm"
							/>
						</div>
						<input
							v-model="timeInputValue"
							:readonly="!isTimeEditable || isMobileVariant"
							:class="{ disabled: isDisabled || !isTimeEditable }"
							class="time-set"
							type="text"
							@change="handleInputChange"
							@click="handleInputClick"
							@keyup.enter="handleTimeInputEnter"
							@keyup.esc="handleTimeInputEscape"
							@focus="onInputFocus"
							@blur="onInputBlur"
						>
						<div
							class="timer-arrow minute-down-arrow"
							:class="{disabled: isDisabled || !isTimeEditable}"
							@click="decrementMinutes()"
						>
							<Icon
								v-if="!isMobileWebApp"
								icon-name="down-arrow"
								size="sm"
							/>
						</div>
						<div
							class="timer-arrow second-down-arrow"
							:class="{disabled: isDisabled || !isTimeEditable}"
							@click="decrementSeconds()"
						>
							<Icon
								v-if="!isMobileWebApp"
								icon-name="down-arrow"
								size="sm"
							/>
						</div>
						<div class="restore-icon">
							<Icon
								v-if="isTimeEditable && currentTime.formattedTime !== timeInputValue"
								icon-name="restore"
								size="sm"
								@click="restoreTime()"
							/>
						</div>
					</div>
				</div>
				<div class="right">
					<label>{{ guestTeamLabel }}</label>
					<TagButton
						class="tag-button"
						:label="$I18n.trans('tag.names.generic.goal')"
						hide-icon
						:label-shadow="false"
						:enabled="!isTimeEditable"
						:clickable="true"
						@click="handleScoreIncrease(guestTeam)"
					/>
					<TagButton
						class="tag-button"
						:label="$I18n.trans('tag.names.generic.2_min')"
						hide-icon
						:label-shadow="false"
						:enabled="!isTimeEditable"
						:clickable="true"
						@click="handleTwoMinPenalty(guestTeam)"
					/>
					<TagButton
						class="tag-button"
						:label="$I18n.trans('tag.names.generic.5_min')"
						hide-icon
						:label-shadow="false"
						:enabled="!isTimeEditable"
						:clickable="true"
						@click="handleFiveMinPenalty(guestTeam)"
					/>
				</div>
			</div>
			<DesktopBottomPenalties
				v-if="!isMobileWebApp"
				:penalties="penalties"
				:home-team-id="homeTeamId"
				:guest-team-id="guestTeamId"
				:is-time-editable="isTimeEditable"
				:current-formatted-time="currentTime.formattedTime"
				:time-input-value="timeInputValue"
				:has-upcoming-clock-start="hasUpcomingClockStart"
				:time-toggle-button-icon="timeToggleButtonIcon"
				@handle-clock-toggle="handleClockToggle"
				@save-time="saveTime"
				@open-latest-tags="openLatestTags"
			/>
			<MobileBottomPenalties
				v-else
				:penalties="penalties"
				:home-team-id="homeTeamId"
				:guest-team-id="guestTeamId"
				:is-time-editable="isTimeEditable"
				:current-formatted-time="currentTime.formattedTime"
				:time-input-value="timeInputValue"
				:has-upcoming-clock-start="hasUpcomingClockStart"
				:time-toggle-button-icon="timeToggleButtonIcon"
				@handle-clock-toggle="handleClockToggle"
				@save-time="saveTime"
				@open-latest-tags="openLatestTags"
			/>
		</div>

		<!-- disabled state overlay -->
		<div
			v-if="isDisabled"
			class="disabled-overlay"
		>
			<div
				v-if="showNotification"
				class="notification"
			>
				<p>{{ transUcfirst('one.scoreboard.ice_hockey_activation_info') }}</p>
			</div>
		</div>

		<!-- mobile: floating time input control -->
		<div
			v-if="showMobileTimeInput"
			class="mobile-time-input"
		>
			<MobileTimeInput
				v-model="timeInputValue"
				:labels="mobileTimeInputLabels"
				@cancel="handleMobileInputCancel"
			/>
		</div>
	</div>
</template>

<script>
import { padStart } from 'lodash-es';
import { getGameTime, getPeriods, getPenalties } from '@js/logic/game/ice_hockey';
import { getScoreZones, getScoreZone } from '@js/logic/game';
import MobileTimeInput from '../../MobileTimeInput/MobileTimeInput.vue';
import TagButton from '@videotools/js/one/components/TagButton/TagButton';
import { mapActions, mapGetters } from 'vuex';
import DesktopBottomPenalties from './DesktopBottomPenalties';
import MobileBottomPenalties from './MobileBottomPenalties';
import { playerBus } from '@videotools/js/events';

const VARIANT = {
	DEFAULT: 'default',
	MOBILE:  'mobile',
};
const STATE = {
	DISABLED: 'disabled',
	ACTIVE:   'active',
	PAUSED:   'paused',
};
const THIRDTIME_START = 'third_start';
const THIRDTIME_END = 'third_end';
const OVERTIME_START = 'overtime_start';
const OVERTIME_END = 'overtime_end';
const PENALTY_SHOOTOUT_START = 'penalty_shootout_start';
const PENALTY_SHOOTOUT_END = 'penalty_shootout_end';
const TIMING_TAG = {
	CLOCK_START: 'clock_start',
	CLOCK_STOP:  'clock_end',
	CLOCK_SET:   'clock_set',
};

const thirdtimeTagNames = new Set([THIRDTIME_START, THIRDTIME_END]);
const overtimeTagNames = new Set([OVERTIME_START, OVERTIME_END]);
const penaltyShootoutTagNames = new Set([PENALTY_SHOOTOUT_START, PENALTY_SHOOTOUT_END]);
const timingTagNames = Object.values(TIMING_TAG);

const splitTime = (time) => {
	let minutes = 0;
	let seconds = 0;
	const parts = time.split(':');
	if (parts.length === 2) {
		minutes = Number.parseInt(parts[0]);
		seconds = Number.parseInt([parts[1]]);
		if (Number.isFinite(minutes) && Number.isFinite(seconds)) {
			return { minutes, seconds };
		}
	}
};

const joinTime = (minutes, seconds) => {
	return `${padStart(Math.max(0, minutes), 2, '0')}:${padStart(Math.max(0, Math.min(59, seconds)), 2, '0')}`;
};

export default {
	name:       'IceHockeyGameControl',
	components: { MobileTimeInput, TagButton, DesktopBottomPenalties, MobileBottomPenalties },
	props:      {
		position: {
			type:     Number,
			required: true,
		},
		event: {
			type:     Object,
			required: true,
		},
		stopClockOnAction: {
			type:    Boolean,
			default: true,
		},
		tags: {
			type:     Array,
			required: true,
		},
		variant: {
			type:    String,
			default: VARIANT.DEFAULT,
		},
		disabled: {
			type: Boolean,
		},
	},
	data() {
		return {
			timeInputValue:      '',
			showMobileTimeInput: false,
			inputFocused:        false,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		timeTooltip() {
			if (this.isActive) return this.$I18n.trans('one.pause_timer_to_edit');
			return null;
		},
		penalties() {
			return getPenalties({
				position:    this.position,
				event:       this.event,
				tags:        this.tags,
				periods:     this.periods,
				homeTeamId:  this.homeTeamId,
				guestTeamId: this.guestTeamId,
			});
		},
		guestTeamId() {
			return this.guestTeam.participatable.id;
		},
		homeTeamId() {
			return this.homeTeam.participatable.id;
		},
		isDisabledFromProp() {
			return this.disabled;
		},
		isMobileVariant() {
			return this.variant === VARIANT.MOBILE;
		},
		hasThirdTimeStartTag() {
			return this.tags.some((eventTag) => eventTag.tag.name === THIRDTIME_START);
		},
		hasOvertimeStartTag() {
			return this.tags.some((eventTag) => eventTag.tag.name === OVERTIME_START);
		},
		sortedTags() {
			const sortedTags = Array.from(this.tags);
			sortedTags.sort((a, b) => a.timestamp - b.timestamp);
			return sortedTags;
		},
		timingTags() {
			return this.sortedTags.filter((eventTag) => {
				return timingTagNames.includes(eventTag.tag.name) && eventTag.timestamp < this.position;
			});
		},
		hasUpcomingClockStart() {
			return this.sortedTags.find(tag => tag.timestamp > this.position)?.name === TIMING_TAG.CLOCK_START;
		},
		thirdtimeTags() {
			return this.sortedTags.filter((eventTag) => thirdtimeTagNames.has(eventTag.tag.name) && eventTag.timestamp < this.position);
		},
		overtimeTags() {
			return this.sortedTags.filter((eventTag) => overtimeTagNames.has(eventTag.tag.name) && eventTag.timestamp < this.position);
		},
		penaltyShootoutTags() {
			return this.sortedTags.filter((eventTag) => penaltyShootoutTagNames.has(eventTag.tag.name) && eventTag.timestamp < this.position);
		},
		state() {
			// Rule 1: State is DISABLED between thirdtime_end and thirdtime_start, or if there is no thirdtime_start.
			if (!this.thirdtimeTags.length) return STATE.DISABLED;
			if (!this.hasThirdTimeStartTag && !this.hasOvertimeStartTag) return STATE.DISABLED;
			const thirdTimeEnded = this.thirdtimeTags.length > 0 && this.thirdtimeTags[this.thirdtimeTags.length - 1].tag.name === THIRDTIME_END;
			const overtimeStarted = this.overtimeTags.length > 0 && this.overtimeTags[this.overtimeTags.length - 1].tag.name === OVERTIME_START;
			const penaltyShootoutStarted = this.penaltyShootoutTags.length > 0 && this.penaltyShootoutTags[this.penaltyShootoutTags.length - 1].tag.name === PENALTY_SHOOTOUT_START;
			if (thirdTimeEnded && !overtimeStarted && !penaltyShootoutStarted) {
				return STATE.DISABLED;
			}

			// Rule 2: State is ACTIVE if there are no CLOCK_START / CLOCK_STOP tags.
			// Rule 3: State is ACTIVE/PAUSED based on the closest CLOCK_START/CLOCK_STOP.
			for (let i = this.timingTags.length - 1; i >= 0; i--) {
				const currentTag = this.timingTags[i];
				if (currentTag.tag.name === TIMING_TAG.CLOCK_START) return STATE.ACTIVE;
				if (currentTag.tag.name === TIMING_TAG.CLOCK_STOP) return STATE.PAUSED;
			}
			return STATE.ACTIVE;
		},
		isDisabled() {
			return this.isDisabledFromProp || this.state === STATE.DISABLED;
		},
		isActive() {
			return !this.isDisabled && this.state === STATE.ACTIVE;
		},
		isPaused() {
			return !this.isDisabled && this.state === STATE.PAUSED;
		},
		wraperClass() {
			return {
				[this.state]: true,
				mobile:       this.isMobileVariant,
				disabled:     this.isDisabled,
			};
		},
		homeTeam() {
			return this.event.participants.find((item) => item.type === 'home');
		},
		guestTeam() {
			return this.event.participants.find((item) => item.type === 'guest');
		},
		scoreZones() {
			return getScoreZones({ event: this.event, tags: this.tags });
		},
		currentScore() {
			const zone = getScoreZone({
				timestamp:  this.position,
				event:      this.event,
				tags:       this.tags,
				scoreZones: this.scoreZones,
			});
			const homeScore = Object.values(zone.score).find((item) => item.participant.id === this.homeTeam.id);
			const guestScore = Object.values(zone.score).find((item) => item.participant.id === this.guestTeam.id);

			return {
				home:  homeScore?.value,
				guest: guestScore?.value,
			};
		},
		periods() {
			return getPeriods({ event: this.event, tags: this.tags });
		},
		currentTime() {
			const { minutes, seconds } = getGameTime({
				position: this.position,
				event:    this.event,
				tags:     this.timingTags,
				periods:  this.periods,
			});

			const formattedTime = `${padStart(minutes, 2, '0')}:${padStart(Math.floor(seconds), 2, '0')}`;

			return {
				minutes,
				seconds,
				formattedTime,
			};
		},
		homeTeamLabel() {
			return this.homeTeam?.participatable.club.code;
		},
		guestTeamLabel() {
			return this.guestTeam?.participatable.club.code;
		},
		homeTeamScore() {
			return this.currentScore.home;
		},
		guestTeamScore() {
			return this.currentScore.guest;
		},
		isTimeEditable() {
			return this.state === STATE.PAUSED;
		},
		timeToggleButtonIcon() {
			if (this.state === STATE.ACTIVE) return 'button-pause-filled';
			return 'button-play-filled';
		},
		showNotification() {
			return !this.isDisabledFromProp && !this.hasThirdTimeStartTag && !this.hasOvertimeStartTag;
		},
		mobileTimeInputLabels() {
			return {
				title:   this.$I18n.trans('one.mobile_time_input_title'),
				confirm: this.$I18n.trans('one.mobile_time_input_save'),
			};
		},
	},
	watch: {
		currentTime(value, prevValue) {
			if (value.formattedTime === prevValue.formattedTime) return;
			this.timeInputValue = value.formattedTime;
		},
		timeInputValue() {
			if (this.isMobileVariant && this.showMobileTimeInput) {
				this.handleInputChange();
				this.showMobileTimeInput = false;
			}
		},
	},
	mounted() {
		this.timeInputValue = this.currentTime.formattedTime;
	},
	methods: {
		...mapActions({
			showAssignPlayerModal: 'scoreboardControl/showAssignPlayerModal',
			increaseScore:         'event/increaseScore',
			setAction2mTag:        'event/setAction2mTag',
			setAction5mTag:        'event/setAction5mTag',
			showLatestTagsModal:   'scoreboardControl/showLatestTagsModal',
		}),
		onInputFocus() {
			if (this.isTimeEditable) this.inputFocused = true;
		},
		onInputBlur() {
			this.inputFocused = false;
		},
		handleTimeInputEnter() {
			if (this.isPaused && this.currentTime.formattedTime !== this.timeInputValue) this.saveTime();
		},
		handleTimeInputEscape() {
			if (this.isTimeEditable && this.currentTime.formattedTime !== this.timeInputValue) this.restoreTime();
		},
		openLatestTags() {
			this.showLatestTagsModal();
		},
		saveTime() {
			const { minutes, seconds } = splitTime(this.timeInputValue);

			const roundedTimestamp = Math.round(this.position);

			this.$emit('clock-set', {
				timestamp: roundedTimestamp,
				value:     minutes * 60 + seconds,
			});

			playerBus.seek(roundedTimestamp + 1);
		},
		restoreTime() {
			this.timeInputValue = this.currentTime.formattedTime;
		},
		incrementSeconds() {
			if (!this.isTimeEditable) return;
			const time = splitTime(this.timeInputValue);
			if (time) {
				let { minutes, seconds } = time;
				if (minutes !== 20) {
					if (seconds !== 59) seconds += 1;
					else {
						seconds = 0;
						minutes += 1;
					}
					this.timeInputValue = joinTime(minutes, seconds);
				}
			}
		},
		incrementMinutes() {
			if (!this.isTimeEditable) return;
			const time = splitTime(this.timeInputValue);
			if (time) {
				let { minutes, seconds } = time;
				if (minutes !== 20) {
					if (minutes === 19) seconds = 0;
					minutes += 1;
					this.timeInputValue = joinTime(minutes, seconds);
				}
			}
		},
		decrementSeconds() {
			if (!this.isTimeEditable) return;
			const time = splitTime(this.timeInputValue);
			if (time) {
				let { minutes, seconds } = time;
				if (seconds !== 0) seconds -= 1;
				else {
					seconds = 59;
					if (minutes > 0) minutes -= 1;
				}
				this.timeInputValue = joinTime(minutes, seconds);
			}
		},
		decrementMinutes() {
			if (!this.isTimeEditable) return;
			const time = splitTime(this.timeInputValue);
			if (time) {
				let { minutes, seconds } = time;
				if (minutes > 0) {
					minutes -= 1;
					this.timeInputValue = joinTime(minutes, seconds);
				}
			}
		},
		handleScoreIncrease(team) {
			if (!this.teamHasPlayers(team)) {
				this.increaseScore({ participant: team, timestamp: Math.round(this.position) });
				if (this.stopClockOnAction) this.handleClockStop(Math.round(this.position));
				return;
			}

			this.showAssignPlayerModal({
				participant:    team,
				timestamp:      Math.round(this.position),
				actionFunction: (data) => {
					this.increaseScore(data);
					if (this.stopClockOnAction) this.handleClockStop(data.timestamp);
				},
				action: 'Goal',
			});
		},
		handleTwoMinPenalty(team) {
			if (!this.teamHasPlayers(team)) {
				this.setAction2mTag({ participant: team, timestamp: Math.round(this.position) });
				if (this.stopClockOnAction) this.handleClockStop(Math.round(this.position));
				return;
			}

			this.showAssignPlayerModal({
				participant:    team,
				timestamp:      Math.round(this.position),
				actionFunction: (data) => {
					this.setAction2mTag(data);
					if (this.stopClockOnAction) this.handleClockStop(data.timestamp);
				},
				action: '2m penalty',
			});
		},
		handleFiveMinPenalty(team) {
			if (!this.teamHasPlayers(team)) {
				this.setAction5mTag({ participant: team, timestamp: Math.round(this.position) });
				if (this.stopClockOnAction) this.handleClockStop(Math.round(this.position));
				return;
			}

			this.showAssignPlayerModal({
				participant:    team,
				timestamp:      Math.round(this.position),
				actionFunction: (data) => {
					this.setAction5mTag(data);
					if (this.stopClockOnAction) this.handleClockStop(data.timestamp);
				},
				action: '5m penalty',
			});
		},
		handleClockStart() {
			const roundedTimestamp = Math.round(this.position);
			this.$emit('clock-start', { timestamp: roundedTimestamp });
			playerBus.seek(roundedTimestamp + 1);
		},
		handleClockStop(position) {
			const { minutes, seconds } = splitTime(this.timeInputValue);
			const roundedTimestamp = Math.round(position);

			this.$emit('clock-stop', {
				timestamp: roundedTimestamp,
				value:     minutes * 60 + seconds,
			});
			playerBus.seek(roundedTimestamp + 1);
		},
		handleClockToggle() {
			if (this.isActive) this.handleClockStop(this.position);
			else if (this.isPaused) this.handleClockStart();
			else throw new Error('Tried to toggle the game time in an invalid state.');
		},
		handleInputChange() {
			let minutes = 0;
			let seconds = 0;
			const parts = this.timeInputValue.split(':');
			if (parts.length === 2) {
				minutes = Number.parseInt(parts[0]);
				seconds = Number.parseInt([parts[1]]);
				if (minutes >= 20) {
					minutes = 20;
					if (seconds > 0) seconds = 0;
				}
				if (Number.isFinite(minutes) && Number.isFinite(seconds)) {
					const value = `${padStart(Math.max(0, minutes), 2, '0')}:${padStart(Math.max(0, Math.min(59, seconds)), 2, '0')}`;
					this.timeInputValue = value;
					return;
				}
			}
			this.timeInputValue = this.currentTime.formattedTime;
		},
		handleInputClick() {
			if (this.isMobileVariant && this.isTimeEditable) this.showMobileTimeInput = true;
		},
		handleMobileInputCancel() {
			this.showMobileTimeInput = false;
		},
		teamHasPlayers(team) {
			return team?.participatable?.players?.length > 0;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@videotools/sass/fonts';

::v-deep {
	.latest-tags-button {
		z-index: 3;
		height: 48px;
		text-align: center;
	}

	.is-active {
		.penalty-timer:not(.grayed) {
			color: var(--color-hype-yellow);
		}
	}

	button.action-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		border-radius: 27px;
		border: 1px solid var(--color-white);
		color:  var(--color-hype-yellow);
		border: 1px solid var(--color-hype-yellow);

		&:disabled {
			background: var(--color-grey-850);
			border-color: var(--color-grey-700);
			color: var(--color-grey-700);
			cursor: auto;
		}
	}

	.bottom-layer-one {
		display: flex;
		justify-content: space-between;
	}

	.bottom-layer-two {
		display: flex;
		justify-content: space-between;
	}
}

.game-control {
	position: relative;
	padding-bottom: 24px;

	.main {
		margin-bottom: 16px;

		.tag-button {
			width: 128px;
			height: 48px;
			text-align: center;

			&:not(:last-child) {
				margin-bottom: 16px;
			}
		}
	}

	.score {
		display: flex;
		width: 144px;
		justify-content: space-between;
		margin-top: 28px;
		margin-bottom: 16px;
	}

	&:hover {
		.controls .main .center .time-background {
			background: var(--color-grey-800);
		}
		.controls .main .center input.time-set {
			background: var(--color-grey-800);
		}
	}

	&.disabled {
		.main {
			opacity: 0.5;
		}
		::v-deep {
			.bottom-layer-one {
				opacity: 0.5;
			}
			.bottom-layer-two div {
				opacity: 0.5;
			}
		}
	}

	.controls {
		display: flex;
		flex-direction: column;

		.main {
			flex: 1;
			display: flex;

			.left,
			.right {
				display: flex;
				flex-direction: column;

				label {
					@include text-sm-2(true);
					margin-bottom: 8px;
				}
			}

			.center {
				flex: 1;
				display: flex;
				flex-direction: column;
				align-items: center;

				.score-value {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 48px;
					height: 48px;
					border: 1px solid var(--color-grey-700);
					border-radius: 2px;
					font-weight: 500;
					font-size: 24px;
					line-height: 32px;
				}

				.time-background {
					background: var(--color-grey-850);
					height: 112px;
					width: 144px;
					display: flex;
					align-items: center;
					position: relative;

					&.focused {
						background: var(--color-black);
						border: 1px solid var(--color-hype-yellow);

						.time-set {
							background: var(--color-black);
						}
					}

					&.disabled {
						cursor: not-allowed;
					}

					&.edited {
						border: 1px solid var(--color-hype-yellow);
					}

					.restore-icon {
						position: absolute;
						cursor: pointer;
						right: 12px;

						&:hover {
							padding: 6px;
							right: 6px;
							background: var(--color-grey-850);
						}
					}

					.timer-arrow {
						position: absolute;
						cursor: pointer;

						&.minute-up-arrow {
							top: 15px;
							left: 45px;

							&:not(.disabled):hover {
								padding: 9px 14px 9px 14px;
								background: var(--color-grey-850);
								top: 6px;
								left: 31px;
							}
						}

						&.second-up-arrow {
							top: 15px;
							left: 82px;

							&:not(.disabled):hover {
								padding: 9px 14px 9px 14px;
								background: var(--color-grey-850);
								top: 6px;
								left: 68px;
							}
						}

						&.minute-down-arrow {
							top: 80px;
							left: 45px;

							&:not(.disabled):hover {
								padding: 9px 14px 9px 14px;
								background: var(--color-grey-850);
								top: 71px;
								left: 31px;
							}
						}

						&.second-down-arrow {
							top: 80px;
							left: 82px;

							&:not(.disabled):hover {
								padding: 9px 14px 9px 14px;
								background: var(--color-grey-850);
								top: 71px;
								left: 68px;
							}
						}

						&.disabled {
							cursor: not-allowed;
						}
					}
				}

				input {
					width: 100%;
					padding: 8px 0;
					border-radius: 2px;
					background: var(--color-grey-900);
					text-align: center;
					font-weight: 500;
					font-size: 24px;
					line-height: 32px;

					&.time-set {
						width: 142px;
						height: 48px;
						background: var(--color-grey-850);

						&.disabled {
							cursor: not-allowed;
						}
					}
				}
			}
		}
	}

	.disabled-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;

		.notification {
			display: flex;
			align-items: flex-start;
			padding: 24px;
			background: var(--color-green-petrol);

			p {
				flex: 1;
			}

			button.close {
				width: 16px;
				height: 16px;
				margin-left: 28px;
				line-height: 0;
				display: grid;
				place-content: center;
			}
		}
	}

	&.active {
		.center {
			.time-set {
				color: var(--color-hype-yellow);
			}
			button {
				border: 1px solid var(--color-hype-yellow);
			}
		}
	}

	&.mobile {
		.tag-button {
			width: 76px;
		}

		.main {
			margin-bottom: 8px;
		}

		.left {
			width: 100%;
		}

		.right {
			width: 100%;
		}

		.center {
			margin-left: 8px;
			margin-right: 8px;
		}
		.controls {
			.tag-button {
				width: auto;
				height: 48px;

				&.latest-tags-button {
					width: 175px;
				}
			}
		}
	}
}

.mobile-time-input {
	position: fixed;
	bottom: 0;
	left: 0;
	width: 100%;
	z-index: 100;
}
</style>
